import { computed, reactive, ref } from 'vue';
import type { DocumentRevision } from '@/entities/Document';
import { documentApi } from '@/entities/Document';

export interface DocumentRevisionGroup {
  documentId: number;
  latestRevision: DocumentRevision;
  olderRevisions: DocumentRevision[];
  hasOlderRevisions: boolean;
}

export type PreviousRevisionsStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface PreviousRevisionsState {
  status: PreviousRevisionsStatus;
  revisions: DocumentRevision[];
}

const IDLE_STATE: PreviousRevisionsState = { status: 'idle', revisions: [] };

interface UseDocumentRevisionGroupsOptions {
  revisions: () => DocumentRevision[];
}

export function useDocumentRevisionGroups(options: UseDocumentRevisionGroupsOptions) {
  const expandedDocumentId = ref<number | null>(null);
  const previousRevisionsByDocumentId = reactive(new Map<number, PreviousRevisionsState>());

  const getPreviousRevisionsState = (documentId: number): PreviousRevisionsState =>
    previousRevisionsByDocumentId.get(documentId) ?? IDLE_STATE;

  const groupedRevisions = computed<DocumentRevisionGroup[]>(() =>
    options.revisions().map((revision) => {
      const state = previousRevisionsByDocumentId.get(revision.documentId);
      return {
        documentId: revision.documentId,
        latestRevision: revision,
        olderRevisions: state?.revisions ?? [],
        hasOlderRevisions:
          state?.status === 'loaded'
            ? state.revisions.length > 0
            : (revision.hasPreviousRevisions ?? false),
      };
    })
  );

  const latestRevisions = computed(() => options.revisions());

  const loadPreviousRevisions = async (documentId: number, excludeRevisionId: number) => {
    previousRevisionsByDocumentId.set(documentId, { status: 'loading', revisions: [] });
    try {
      const revisions = await documentApi.getPreviousRevisions(documentId, excludeRevisionId);
      previousRevisionsByDocumentId.set(documentId, { status: 'loaded', revisions });
    } catch {
      previousRevisionsByDocumentId.set(documentId, { status: 'error', revisions: [] });
    }
  };

  const handleToggleExpand = (documentId: number, excludeRevisionId: number) => {
    if (expandedDocumentId.value === documentId) {
      expandedDocumentId.value = null;
      return;
    }

    expandedDocumentId.value = documentId;
    const state = getPreviousRevisionsState(documentId);
    if (state.status === 'idle' || state.status === 'error') {
      void loadPreviousRevisions(documentId, excludeRevisionId);
    }
  };

  const retryLoadPreviousRevisions = (documentId: number, excludeRevisionId: number) => {
    void loadPreviousRevisions(documentId, excludeRevisionId);
  };

  const isDocumentExpanded = (documentId: number) => expandedDocumentId.value === documentId;

  const handleRevisionsDeleted = (deletedIds: number[]) => {
    if (deletedIds.length === 0) return;
    const deletedIdSet = new Set(deletedIds);

    for (const group of groupedRevisions.value) {
      if (deletedIdSet.has(group.latestRevision.id)) {
        previousRevisionsByDocumentId.delete(group.documentId);
        if (expandedDocumentId.value === group.documentId) {
          expandedDocumentId.value = null;
        }
      }
    }

    for (const [documentId, state] of previousRevisionsByDocumentId.entries()) {
      if (state.status !== 'loaded') continue;
      if (!state.revisions.some((revision) => deletedIdSet.has(revision.id))) continue;

      previousRevisionsByDocumentId.set(documentId, {
        status: 'loaded',
        revisions: state.revisions.filter((revision) => !deletedIdSet.has(revision.id)),
      });
    }
  };

  const invalidatePreviousRevisions = (documentId: number) => {
    previousRevisionsByDocumentId.delete(documentId);
  };

  const invalidateAllPreviousRevisions = () => {
    previousRevisionsByDocumentId.clear();
  };

  return {
    groupedRevisions,
    latestRevisions,
    expandedDocumentId,
    handleToggleExpand,
    isDocumentExpanded,
    getPreviousRevisionsState,
    retryLoadPreviousRevisions,
    handleRevisionsDeleted,
    invalidatePreviousRevisions,
    invalidateAllPreviousRevisions,
  };
}
